import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, Loader2, Plus } from 'lucide-react';
import { api } from '../../api/client';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  type TripInspectionListRow, DRIVER_NOT_LINKED_FALLBACK, isDriverNotLinked, reportOutcome, tripErrorMessage, tripTypeLabel,
} from '../../lib/trip-inspections';
import { OutcomeBadge } from '../../pages/trucking/TripInspectionsScreen';
import TripPageChrome from '../../pages/trucking/TripPageChrome';

/**
 * A driver's own trip inspections, and the way to file one.
 *
 * Three states, never two (CLAUDE.md): reports, none yet, or why the list did
 * not arrive. The fourth is the one this screen exists to get right — a login
 * with no driver record is refused with a 409 whose sentence says to ask the
 * office, and that sentence is shown in place of the list AND in place of the
 * New button, because filing would be refused for the same reason.
 */
export default function FieldTripInspectionsScreen() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const query = useQuery<TripInspectionListRow[]>({
    queryKey: ['trip-inspections', 'mine'],
    queryFn: () => api.trucking.tripInspections.list().then((r) => r.data?.data ?? []),
    retry: (count, err) => !isDriverNotLinked(err) && count < 2,
  });
  const notLinked = isDriverNotLinked(query.error);
  const failure = notLinked ? null : queryLoadFailure(query);
  const rows = query.data ?? [];

  return (
    <TripPageChrome title="Trip Inspections" eyebrow="Fleet safety" onBack={() => navigate(-1)}>
      <div className="space-y-4">
        {notLinked ? (
          <div role="alert" data-testid="trip-not-linked" className="flex items-start gap-3 rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
            <p className="text-sm text-fieldiq-text">{tripErrorMessage(query.error, DRIVER_NOT_LINKED_FALLBACK)}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => navigate('/field/trip-inspections/new?type=pre_trip')} className="fi-action fi-action--standard fi-action--primary justify-center">
              <Plus className="size-4" aria-hidden="true" /> Pre-trip
            </button>
            <button type="button" onClick={() => navigate('/field/trip-inspections/new?type=post_trip')} className="fi-action fi-action--standard fi-action--primary justify-center">
              <Plus className="size-4" aria-hidden="true" /> Post-trip
            </button>
          </div>
        )}
        {query.isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-fieldiq-muted" /></div>
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="your trip inspections" />
        ) : notLinked ? null : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-fieldiq-text">You have not filed a trip inspection yet.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => navigate(`/field/trip-inspections/${r.id}`)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 text-left">
                  <span>
                    <span className="block text-sm font-bold text-fieldiq-text">{r.units.map((u) => u.unit_number).join(', ') || '—'}</span>
                    <span className="block text-xs text-fieldiq-muted">{tripTypeLabel(r.inspection_type)} · {formatInstant(r.inspected_at, timeZone) ?? '—'} · {r.location || '—'}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <OutcomeBadge outcome={reportOutcome(r)} />
                    <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </TripPageChrome>
  );
}
